import { forwardRef, useContext, useState } from "react";
import { FiMoreHorizontal } from "react-icons/fi";
import OptionsItems from "./OptionsItems/OptionsItems.tsx";
import EditingState from "./EditingState/EditingState.tsx";
import DeleteConfirmationState from "./DeleteConfirmationState/DeleteConfirmationState.tsx";
import { handleEditClick, handleSaveEditClick, handleCancelEditClick, handleSaveDeleteClick, handleCancelDeleteClick, handleDropdownToggleNoProp, handleChatClick, handleDeleteClick, handleInputChange, handleFavoriteClick } from "./Utils";
import { GlobalContext } from "../../../../App.tsx";
import {useAppStore} from "../../../../GlobalStore/GlobalStore.ts";

interface ChatListItemProps {
    chat: any;
    isDropdownOpen: boolean;
    onDropdownToggle: (chatId: string) => void;
    isEditing: boolean;
    setActiveDropdownId: any;
    setEditingChatId: any;
}

const ChatListItem = forwardRef<HTMLLIElement, ChatListItemProps>((
    {
        chat,
        isDropdownOpen,
        onDropdownToggle,
        isEditing,
        setActiveDropdownId,
        setEditingChatId
    }, ref) => {
    const { messagesSequenceRef, messagesRef, currentMessageRef, setMessages, resetStateHelper } = useContext(GlobalContext);

    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const { isIncognito } = settingsObj;
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { chatHistory } = dynamicStateObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const updateChatHistory = useAppStore((state: any) => state.updateChatHistory);
    const updateImportantMessageIds = useAppStore((state: any) => state.updateImportantMessageIds);
    const userObj = useAppStore((state: any) => state.userObj);
    const { user } = userObj;
    const totalChats = chatHistory.length;

    const [editedChatName, setEditedChatName] = useState<string>(chat.name);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const shouldOpenUpwards = totalChats >= 10 && chat.id >= totalChats - 3;

    const renderDropdown = () => {
        if (isDropdownOpen) {
            return (
                <div
                    className={`absolute right-0 ${shouldOpenUpwards ? 'bottom-full mb-1' : 'top-full mt-1'} mx-auto py-1 w-48 bg-neutral-100 dark:bg-neutral-800 rounded-md shadow-lg z-10`}
                >
                    <OptionsItems
                        handleDeleteClick={(event: any) => handleDeleteClick(event, setShowDeleteConfirmation)}
                        handleEditClick={(event: any) => handleEditClick(event, chat.id, setEditingChatId, setActiveDropdownId)}
                        handleFavoriteClick={(event: any) => handleFavoriteClick(event, chat.id, chatHistory, updateChatHistory, user, setActiveDropdownId)}
                        chatId={chat.id}
                    />
                </div>
            );
        }
    };

    const renderDefaultState = () => {
        return (
            <>
                <span className={`truncate max-w-[85%]`}>{chat.name}</span>
                <div className="relative">
                    <button
                        onClick={(event: any) => handleDropdownToggleNoProp(event, onDropdownToggle, chat)}
                        className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full"
                    >
                        <FiMoreHorizontal />
                    </button>
                    {renderDropdown()}
                </div>
            </>
        );
    };

    return (
        <li
            onClick={isIncognito ? undefined : () => {
                handleChatClick(setEditingChatId, chat, messagesRef, currentMessageRef, messagesSequenceRef, setDynamicStateObj, setMessages, updateImportantMessageIds, user, resetStateHelper);
                setDynamicStateObj({ isMenuOpen: false });
            }}
            ref={ref}
            key={chat.id}
            className={`cursor-pointer rounded hover:bg-neutral-400 dark:hover:bg-neutral-800 hover:bg-opacity-50 dark:hover:bg-opacity-75 ${dynamicStateObj.conversationId === chat.id ? "bg-neutral-400 dark:bg-neutral-800 bg-opacity-80 dark:bg-opacity-80" : ""} py-2 px-4 flex justify-between items-center relative ${isIncognito ? 'pointer-events-none' : ''}`}
        >
            {isEditing ? (
                <EditingState
                    editedChatName={editedChatName}
                    handleInputChange={(event: any) => handleInputChange(event, setEditedChatName)}
                    handleSaveEditClick={(event: any) => handleSaveEditClick(event, chat.id, editedChatName, updateChatHistory, user, setEditingChatId)}
                    handleCancelEditClick={(event: any) => handleCancelEditClick(event, chat, setEditedChatName, setEditingChatId, setShowDeleteConfirmation)}
                />
            ) : showDeleteConfirmation ? (
                <DeleteConfirmationState
                    handleSaveDeleteClick={(event: any) => handleSaveDeleteClick(event, chat.id, dynamicStateObj.conversationId, setActiveDropdownId, setShowDeleteConfirmation,  updateChatHistory, user, resetStateHelper)}
                    handleCancelDeleteClick={(event: any) => handleCancelDeleteClick(event, setActiveDropdownId, setShowDeleteConfirmation)}
                />
            ) : renderDefaultState()}
        </li>
    );
});

export default ChatListItem;
